import clamp from "lodash/clamp";
import last from "lodash/last";
import { Day } from "PFApp/booking/parts/overview/booking_module_calendar/booking_calendar/booking_calendar_store";
import {
  addDays,
  getDaysDifference,
  setTimeToMidnight
} from "PFApp/booking/parts/overview/calendar/calendar.utils";

import { DnDMode } from "../../../bars/booking_bar/use_booking_bar_dnd";

type DaysLimits = {
  maxDaysToLeft: number;
  maxDaysToRight: number;
};

export type GetUpdatedDatesReturn = {
  startDate: Date;
  endDate: Date;
  daysDiffLimited: number;
};

const copyDate = (date: Date): Date => new Date(date.getTime());

const getMidnight = (date: Date): Date => setTimeToMidnight(copyDate(date));

const getMinDate = (days: Day[], pastDaysLimit: number): Date => {
  const today = days.find(({ isToday }) => isToday)?.date ?? setTimeToMidnight(new Date());
  return setTimeToMidnight(addDays(copyDate(today), -1 * pastDaysLimit));
};

type GetDaysLimits = {
  mode: DnDMode;
  start: Date;
  end: Date;
  days: Day[];
  pastDaysLimit: number;
};

const getDaysLimits = ({ mode, start, end, days, pastDaysLimit }: GetDaysLimits): DaysLimits => {
  const daysFromStartToMinDate = getDaysDifference(getMidnight(start), getMinDate(days, pastDaysLimit));
  const daysFromEndToCalendarEnd = getDaysDifference(getMidnight(end), last(days)!.date);

  switch (mode) {
    case DnDMode.StartEdge:
      return {
        maxDaysToLeft: Math.min(daysFromStartToMinDate, 0),
        maxDaysToRight: Math.max(getDaysDifference(getMidnight(start), getMidnight(end)), 0)
      };
    case DnDMode.EndEdge:
      return {
        maxDaysToLeft: Math.min(getDaysDifference(getMidnight(end), getMidnight(start)), 0),
        maxDaysToRight: Math.max(daysFromEndToCalendarEnd, 0)
      };
    case DnDMode.Horizontal:
    case DnDMode.HorizontalVertical:
      return {
        maxDaysToLeft: Math.min(daysFromStartToMinDate, 0),
        maxDaysToRight: Math.max(daysFromEndToCalendarEnd, 0)
      };
  }
};

export const getUpdatedDates = (
  mode: DnDMode,
  daysDiff: number,
  bookingStartDate: string,
  bookingEndDate: string,
  days: Day[],
  pastDaysLimit: number
): GetUpdatedDatesReturn => {
  const start = new Date(bookingStartDate);
  const end = new Date(bookingEndDate);
  const { maxDaysToLeft, maxDaysToRight } = getDaysLimits({ mode, start, end, days, pastDaysLimit });
  const daysDiffLimited = clamp(daysDiff, maxDaysToLeft, maxDaysToRight);

  return {
    startDate: mode === DnDMode.EndEdge ? start : addDays(start, daysDiffLimited),
    endDate: mode === DnDMode.StartEdge ? end : addDays(end, daysDiffLimited),
    daysDiffLimited
  };
};
